import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { db } from '../config/firebase';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { ShoppingCart, ArrowLeft, Minus, Plus } from 'lucide-react';
import { formatCurrency } from '../utils/formatCurrency';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { currentUser } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const docRef = doc(db, 'products', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setProduct({ id: docSnap.id, ...docSnap.data() });
      } else {
        console.error('Product not found');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    // Check if user is logged in
    if (!currentUser) {
      toast.error('Vui lòng đăng nhập để thêm sản phẩm vào giỏ hàng');
      navigate('/login');
      return;
    }

    addToCart(product, quantity);
    toast.success(`Đã thêm ${quantity} sản phẩm vào giỏ hàng`);
    // Navigate to cart after a short delay
    setTimeout(() => {
      navigate('/cart');
    }, 1000);
  };

  const incrementQuantity = () => setQuantity(prev => prev + 1);
  const decrementQuantity = () => setQuantity(prev => Math.max(1, prev - 1));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Không Tìm Thấy Sản Phẩm</h2>
          <button
            onClick={() => navigate('/products')}
            className="text-primary-600 hover:underline"
          >
            Quay Lại Sản Phẩm
          </button>
        </div>
      </div>
    );
  }

  const images = product.images || [product.imageUrl || 'https://via.placeholder.com/600'];

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.name,
    "image": images,
    "description": product.description || `${product.name} - Kính mắt cao cấp từ Kính mắt Nhân Văn`,
    "brand": {
      "@type": "Brand",
      "name": "Kính mắt Nhân Văn"
    },
    "offers": {
      "@type": "Offer",
      "url": window.location.href,
      "priceCurrency": "VND",
      "price": product.price,
      "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "Kính mắt Nhân Văn"
      }
    },
    "category": product.category
  };

  return (
    <>
      <Helmet>
        <title>{product.name} | Kính mắt Nhân Văn</title>
        <meta name="description" content={product.description || `Mua ${product.name} tại Kính mắt Nhân Văn. ${product.category === 'sunglasses' ? 'Kính mát' : 'Kính cận'} chất lượng cao với bảo vệ UV và thiết kế thời trang.`} />
        <meta name="keywords" content={`${product.name}, ${product.category}, kính mắt, kính, mua online`} />

        {/* Open Graph */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={product.name} />
        <meta property="og:description" content={product.description || `Mua ${product.name} tại Kính mắt Nhân Văn`} />
        <meta property="og:image" content={images[0]} />
        <meta property="og:url" content={window.location.href} />
        <meta property="product:price:amount" content={product.price} />
        <meta property="product:price:currency" content="VND" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-primary-600 mb-6 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Quay Lại
        </button>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
            {/* Images */}
            <div>
              {/* Main Image */}
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-4">
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Thumbnail Images */}
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 ${
                        selectedImage === index ? 'border-primary-600' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              {/* Category */}
              <div className="mb-2">
                <span className="inline-block bg-primary-100 text-primary-600 text-sm px-3 py-1 rounded">
                  {product.category}
                </span>
              </div>

              {/* Name */}
              <h1 className="text-3xl font-bold mb-4">{product.name}</h1>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-baseline gap-4">
                  <p className="text-4xl font-bold text-primary-600">
                    {formatCurrency(product.price)}
                  </p>
                  {product.originalPrice && (
                    <p className="text-xl text-gray-400 line-through">
                      {formatCurrency(product.originalPrice)}
                    </p>
                  )}
                  {product.discount && (
                    <span className="bg-red-500 text-white px-2 py-1 rounded text-sm font-semibold">
                      -{product.discount}%
                    </span>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2">Mô Tả</h2>
                <p className="text-gray-600 leading-relaxed">
                  {product.description || 'Chưa có mô tả.'}
                </p>
              </div>

              {/* Specifications */}
              {product.specifications && (
                <div className="mb-6">
                  <h2 className="text-xl font-semibold mb-2">Thông Số Kỹ Thuật</h2>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <dl className="space-y-2">
                      {Object.entries(product.specifications).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <dt className="text-gray-600">{key}:</dt>
                          <dd className="font-medium">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>
              )}

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">Số Lượng</label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-gray-300 rounded-lg">
                    <button
                      onClick={decrementQuantity}
                      className="p-2 hover:bg-gray-100 transition"
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                    <span className="px-6 py-2 font-semibold">{quantity}</span>
                    <button
                      onClick={incrementQuantity}
                      className="p-2 hover:bg-gray-100 transition"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  {product.stock && (
                    <span className="text-gray-600 text-sm">
                      {product.stock} sản phẩm có sẵn
                    </span>
                  )}
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                className="w-full bg-primary-600 text-white py-4 rounded-lg font-semibold hover:bg-primary-700 transition flex items-center justify-center gap-2 text-lg"
              >
                <ShoppingCart className="w-6 h-6" />
                Thêm Vào Giỏ
              </button>

              {/* Additional Info */}
              <div className="mt-6 border-t pt-6">
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Hỗ Trợ Thanh Toán Khi Nhận Hàng</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Chính Sách Đổi Trả 7 Ngày</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Cam Kết Chất Lượng</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default ProductDetail;
